import { useState, useEffect, useRef } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import PageTransition from '../../components/base/PageTransition';
import SEOHead from '../../components/base/SEOHead';

type SectionId = 'overview' | 'whatsapp' | 'telegram' | 'discord' | 'slack' | 'mattermost' | 'signal' | 'imessage';

interface NavItem {
  id: SectionId;
  label: string;
  icon: string;
}

interface TocItem {
  id: string;
  label: string;
  level: number;
}

const navItems: NavItem[] = [
  { id: 'overview', label: '通道概览', icon: 'ri-layout-grid-line' },
  { id: 'whatsapp', label: 'WhatsApp', icon: 'ri-whatsapp-line' },
  { id: 'telegram', label: 'Telegram', icon: 'ri-telegram-line' },
  { id: 'discord', label: 'Discord', icon: 'ri-discord-line' },
  { id: 'slack', label: 'Slack', icon: 'ri-slack-line' },
  { id: 'mattermost', label: 'Mattermost', icon: 'ri-chat-3-line' },
  { id: 'signal', label: 'Signal', icon: 'ri-shield-keyhole-line' },
  { id: 'imessage', label: 'iMessage', icon: 'ri-message-2-line' },
];

const tocMap: Record<SectionId, TocItem[]> = {
  overview: [
    { id: 'ov-intro', label: '简介', level: 1 },
    { id: 'ov-list', label: '支持的渠道', level: 1 },
    { id: 'ov-notes', label: '注意事项', level: 1 },
    { id: 'ov-gateway', label: 'Gateway 网关', level: 1 },
  ],
  whatsapp: [
    { id: 'wa-intro', label: '简介', level: 1 },
    { id: 'wa-setup', label: '快速设置', level: 1 },
    { id: 'wa-config', label: '配置示例', level: 1 },
    { id: 'wa-options', label: '配置参考', level: 1 },
    { id: 'wa-notes', label: '注意事项', level: 1 },
  ],
  telegram: [
    { id: 'tg-intro', label: '简介', level: 1 },
    { id: 'tg-setup', label: '快速设置', level: 1 },
    { id: 'tg-config', label: '配置示例', level: 1 },
    { id: 'tg-options', label: '配置参考', level: 1 },
    { id: 'tg-features', label: '高级功能', level: 1 },
  ],
  discord: [
    { id: 'dc-intro', label: '简介', level: 1 },
    { id: 'dc-setup', label: '快速设置', level: 1 },
    { id: 'dc-config', label: '配置示例', level: 1 },
    { id: 'dc-options', label: '配置参考', level: 1 },
    { id: 'dc-features', label: '高级功能', level: 1 },
  ],
  slack: [
    { id: 'sl-intro', label: '简介', level: 1 },
    { id: 'sl-setup', label: '快速设置', level: 1 },
    { id: 'sl-config', label: '配置示例', level: 1 },
    { id: 'sl-options', label: '配置参考', level: 1 },
    { id: 'sl-modes', label: '连接模式', level: 1 },
  ],
  mattermost: [
    { id: 'mm-intro', label: '简介', level: 1 },
    { id: 'mm-install', label: '安装插件', level: 1 },
    { id: 'mm-setup', label: '快速设置', level: 1 },
    { id: 'mm-config', label: '配置示例', level: 1 },
    { id: 'mm-options', label: '配置参考', level: 1 },
  ],
  signal: [
    { id: 'sg-intro', label: '简介', level: 1 },
    { id: 'sg-prereq', label: '前置要求', level: 1 },
    { id: 'sg-setup', label: '快速设置', level: 1 },
    { id: 'sg-config', label: '配置示例', level: 1 },
    { id: 'sg-options', label: '配置参考', level: 1 },
  ],
  imessage: [
    { id: 'im-intro', label: '简介', level: 1 },
    { id: 'im-prereq', label: '前置要求', level: 1 },
    { id: 'im-setup', label: '快速设置', level: 1 },
    { id: 'im-config', label: '配置示例', level: 1 },
    { id: 'im-options', label: '配置参考', level: 1 },
  ],
};

function CodeBlock({ code, language = 'json' }: { code: string; language?: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="relative group my-5">
      <div className="flex items-center justify-between bg-slate-800 rounded-t-xl px-5 py-2.5 border border-slate-700 border-b-0">
        <span className="text-sm text-slate-400 font-mono">{language}</span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-400 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
          {copied ? '已复制' : '复制'}
        </button>
      </div>
      <pre className="bg-slate-950 border border-slate-700 rounded-b-xl p-5 overflow-x-auto">
        <code className="text-base text-slate-300 font-mono leading-loose">{code}</code>
      </pre>
    </div>
  );
}

function Badge({ children, color = 'cyan' }: { children: React.ReactNode; color?: string }) {
  const colors: Record<string, string> = {
    cyan: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30',
    amber: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
    emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
    rose: 'bg-rose-500/10 text-rose-400 border-rose-500/30',
    slate: 'bg-slate-500/10 text-slate-400 border-slate-500/30',
  };
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${colors[color] || colors.cyan}`}>
      {children}
    </span>
  );
}

function InfoBox({ type = 'info', children }: { type?: 'info' | 'warning' | 'tip'; children: React.ReactNode }) {
  const styles = {
    info: { bg: 'bg-cyan-500/5 border-cyan-500/20', icon: 'ri-information-line', iconColor: 'text-cyan-400', titleColor: 'text-cyan-400', title: '提示' },
    warning: { bg: 'bg-amber-500/5 border-amber-500/20', icon: 'ri-alert-line', iconColor: 'text-amber-400', titleColor: 'text-amber-400', title: '注意' },
    tip: { bg: 'bg-emerald-500/5 border-emerald-500/20', icon: 'ri-lightbulb-line', iconColor: 'text-emerald-400', titleColor: 'text-emerald-400', title: '技巧' },
  };
  const s = styles[type];
  return (
    <div className={`${s.bg} border rounded-xl p-5 my-5`}>
      <div className="flex items-start gap-3">
        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
          <i className={`${s.icon} ${s.iconColor} text-xl`}></i>
        </div>
        <div className="text-base text-slate-300 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function SectionTitle({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <h2 id={id} className="text-2xl font-bold text-white mt-12 mb-5 flex items-center gap-2.5 scroll-mt-24">
      <span className="w-1.5 h-6 bg-cyan-400 rounded-full inline-block"></span>
      {children}
    </h2>
  );
}

function SubTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-lg font-semibold text-slate-200 mt-8 mb-4">{children}</h3>
  );
}

function ConfigTable({ rows }: { rows: { key: string; type: string; desc: string; default?: string }[] }) {
  return (
    <div className="overflow-x-auto my-5">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-slate-700">
            <th className="text-left py-3 px-4 text-slate-400 font-medium text-base w-1/4">参数</th>
            <th className="text-left py-3 px-4 text-slate-400 font-medium text-base w-1/6">类型</th>
            <th className="text-left py-3 px-4 text-slate-400 font-medium text-base">说明</th>
            <th className="text-left py-3 px-4 text-slate-400 font-medium text-base w-1/5">默认值</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-slate-800/60 hover:bg-slate-800/30 transition-colors">
              <td className="py-3 px-4">
                <code className="text-cyan-400 font-mono text-sm">{row.key}</code>
              </td>
              <td className="py-3 px-4">
                <code className="text-amber-400 font-mono text-sm">{row.type}</code>
              </td>
              <td className="py-3 px-4 text-slate-300 text-base">{row.desc}</td>
              <td className="py-3 px-4">
                {row.default ? (
                  <code className="text-slate-400 font-mono text-sm">{row.default}</code>
                ) : (
                  <span className="text-slate-600">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Section Contents ────────────────────────────────────────────────────────

function OverviewContent() {
  const allChannels = [
    { name: 'BlueBubbles', icon: 'ri-apple-line', status: 'stable' },
    { name: 'Discord', icon: 'ri-discord-line', status: 'stable' },
    { name: '飞书', icon: 'ri-message-3-line', status: 'stable' },
    { name: 'Google Chat', icon: 'ri-google-line', status: 'stable' },
    { name: 'iMessage (旧版)', icon: 'ri-message-2-line', status: 'stable' },
    { name: 'LINE', icon: 'ri-chat-smile-2-line', status: 'stable' },
    { name: 'Matrix', icon: 'ri-grid-line', status: 'stable' },
    { name: 'Mattermost', icon: 'ri-chat-3-line', status: 'stable' },
    { name: 'Microsoft Teams', icon: 'ri-microsoft-line', status: 'stable' },
    { name: 'Nextcloud Talk', icon: 'ri-cloud-line', status: 'stable' },
    { name: 'Nostr', icon: 'ri-flashlight-line', status: 'beta' },
    { name: 'Signal', icon: 'ri-shield-keyhole-line', status: 'stable' },
    { name: 'Slack', icon: 'ri-slack-line', status: 'stable' },
    { name: 'Telegram', icon: 'ri-telegram-line', status: 'stable' },
    { name: 'Tlon', icon: 'ri-planet-line', status: 'beta' },
    { name: 'Twitch', icon: 'ri-twitch-line', status: 'stable' },
    { name: 'WebChat', icon: 'ri-global-line', status: 'stable' },
    { name: 'WhatsApp', icon: 'ri-whatsapp-line', status: 'stable' },
    { name: 'Zalo', icon: 'ri-chat-1-line', status: 'stable' },
    { name: 'Zalo Personal', icon: 'ri-user-line', status: 'beta' },
  ];

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-white mb-4">消息渠道</h1>
        <p className="text-slate-400 text-base leading-relaxed">
          OpenClaw 通过 Gateway 网关支持 20+ 个消息渠道，让您的 AI 智能体无缝接入各类即时通讯平台。
        </p>
      </div>

      <SectionTitle id="ov-intro">简介</SectionTitle>
      <p className="text-slate-300 text-base leading-relaxed mb-5">
        渠道（Channel）是 OpenClaw 与外部消息平台之间的桥梁。每个渠道对应一个即时通讯平台，
        负责接收用户消息并将 AI 回复发送回去。OpenClaw 支持同时运行多个渠道，
        您可以在同一个实例中同时接入 Telegram、Discord、Slack 等多个平台。
      </p>
      <InfoBox type="tip">
        <strong>最快上手：</strong>Telegram 是最容易配置的渠道，只需一个 Bot Token 即可启动，推荐新用户从 Telegram 开始。
      </InfoBox>

      <SectionTitle id="ov-list">支持的渠道</SectionTitle>
      <p className="text-slate-300 text-base leading-relaxed mb-5">
        以下是 OpenClaw 目前支持的所有消息渠道：
      </p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 my-5">
        {allChannels.map((ch) => (
          <div key={ch.name} className="flex items-center gap-3 bg-slate-800/40 border border-slate-700/50 rounded-xl px-4 py-3 hover:border-cyan-500/30 transition-colors">
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
              <i className={`${ch.icon} text-cyan-400 text-lg`}></i>
            </div>
            <div className="min-w-0">
              <div className="text-base text-slate-200 font-medium truncate">{ch.name}</div>
              <div className="text-xs text-slate-500">{ch.status === 'stable' ? '稳定版' : '测试版'}</div>
            </div>
          </div>
        ))}
      </div>

      <SectionTitle id="ov-notes">注意事项</SectionTitle>
      <ul className="space-y-4 text-base text-slate-300">
        <li className="flex items-start gap-3">
          <i className="ri-error-warning-line text-amber-400 mt-1 flex-shrink-0 text-lg"></i>
          <span>支持多账户：可在配置中添加多个 WhatsApp 渠道实例，每个实例对应一个号码。</span>
        </li>
        <li className="flex items-start gap-3">
          <i className="ri-error-warning-line text-amber-400 mt-1 flex-shrink-0 text-lg"></i>
          <span>群组历史注入：首次加入群组时，OpenClaw 会自动注入历史消息以提供上下文。</span>
        </li>
        <li className="flex items-start gap-3">
          <i className="ri-error-warning-line text-amber-400 mt-1 flex-shrink-0 text-lg"></i>
          <span>会话持久化：配对信息会保存在本地，重启后无需重新扫码。</span>
        </li>
        <li className="flex items-start gap-3">
          <i className="ri-error-warning-line text-amber-400 mt-1 flex-shrink-0 text-lg"></i>
          <span>建议使用专用号码，避免使用个人主号，以降低封号风险。</span>
        </li>
      </ul>
    </div>
  );
}

function WhatsAppContent() {
  return (
    <div>
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 flex items-center justify-center bg-emerald-500/10 rounded-xl border border-emerald-500/20">
            <i className="ri-whatsapp-line text-emerald-400 text-2xl"></i>
          </div>
          <div>
            <h1 className="text-4xl font-bold text-white">WhatsApp</h1>
            <div className="flex items-center gap-2 mt-2">
              <Badge color="emerald">稳定版</Badge>
              <Badge color="slate">通过 Baileys</Badge>
            </div>
          </div>
        </div>
        <p className="text-slate-400 text-base leading-relaxed">
          通过 Baileys 库实现 WhatsApp Web 协议接入，支持个人号码和专用号码两种模式。
        </p>
      </div>

      <SectionTitle id="wa-intro">简介</SectionTitle>
      <p className="text-slate-300 text-base leading-relaxed mb-4">
        WhatsApp 渠道使用 Baileys 库通过 WhatsApp Web 协议与 WhatsApp 服务器通信。
        首次连接时需要扫描二维码完成配对，之后会话信息会被持久化保存。
      </p>
      <InfoBox type="warning">
        WhatsApp 渠道使用非官方 API，存在被封号风险。建议使用专用号码而非个人主号，并遵守 WhatsApp 使用条款。
      </InfoBox>

      <SectionTitle id="wa-setup">快速设置</SectionTitle>
      <ol className="space-y-4 text-base text-slate-300 list-none">
        {[
          '在配置文件中添加 WhatsApp 渠道配置（见下方示例）',
          '启动 OpenClaw，终端会显示二维码',
          '使用 WhatsApp 手机端扫描二维码完成配对',
          '配对成功后，渠道即可正常使用',
        ].map((step, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="w-7 h-7 flex items-center justify-center bg-cyan-500/10 text-cyan-400 rounded-full text-sm font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
            <span>{step}</span>
          </li>
        ))}
      </ol>

      <SectionTitle id="wa-config">配置示例</SectionTitle>
      <CodeBlock language="json5" code={`{
  channels: [
    {
      type: "whatsapp",
      // 私信策略：allow（允许）/ block（拒绝）
      dmPolicy: "allow",
      // 自聊模式：off / send / receive / both
      selfChatMode: "off",
      // 允许接收消息的号码白名单（留空表示允许所有）
      allowFrom: [],
      // 媒体文件最大大小（MB）
      mediaMaxMb: 10,
      // 已读回执（true 表示自动标记已读）
      sendReadReceipts: true,
      // 确认表情（消息处理中显示的表情）
      ackReaction: "👀",
    }
  ]
}`} />

      <SectionTitle id="wa-options">配置参考</SectionTitle>
      <ConfigTable rows={[
        { key: 'type', type: 'string', desc: '渠道类型，固定为 "whatsapp"', default: '"whatsapp"' },
        { key: 'dmPolicy', type: 'string', desc: '私信策略：allow（允许）/ block（拒绝）', default: '"allow"' },
        { key: 'selfChatMode', type: 'string', desc: '自聊模式：off / send / receive / both', default: '"off"' },
        { key: 'allowFrom', type: 'string[]', desc: '允许接收消息的号码白名单，留空表示允许所有', default: '[]' },
        { key: 'mediaMaxMb', type: 'number', desc: '媒体文件最大大小（MB）', default: '10' },
        { key: 'sendReadReceipts', type: 'boolean', desc: '是否自动发送已读回执', default: 'true' },
        { key: 'ackReaction', type: 'string', desc: '消息处理中显示的确认表情', default: '"👀"' },
      ]} />
    </div>
  );
}

function TelegramContent() {
  return (
    <div>
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 flex items-center justify-center bg-cyan-500/10 rounded-xl border border-cyan-500/20">
            <i className="ri-telegram-line text-cyan-400 text-2xl"></i>
          </div>
          <div>
            <h1 className="text-4xl font-bold text-white">Telegram</h1>
            <div className="flex items-center gap-2 mt-2">
              <Badge color="emerald">稳定版</Badge>
              <Badge color="slate">通过 grammY</Badge>
            </div>
          </div>
        </div>
        <p className="text-slate-400 text-base leading-relaxed">
          通过 grammY 库使用 Telegram Bot API，支持长轮询和 Webhook 两种连接方式。
        </p>
      </div>

      <SectionTitle id="tg-intro">简介</SectionTitle>
      <p className="text-slate-300 text-base leading-relaxed mb-4">
        Telegram 是 OpenClaw 中最容易配置的渠道。只需从 BotFather 获取一个 Bot Token，
        即可在几分钟内完成配置。支持私聊、群组、话题（论坛超级群组）等多种场景。
      </p>
      <InfoBox type="tip">
        Telegram 是新用户的首选渠道，配置最简单，功能最完整，推荐优先尝试。
      </InfoBox>

      <SectionTitle id="tg-setup">快速设置</SectionTitle>
      <ol className="space-y-4 text-base text-slate-300 list-none">
        {[
          '在 Telegram 中搜索 @BotFather，发送 /newbot 创建新机器人',
          '按提示设置机器人名称和用户名，获取 Bot Token',
          '将 Bot Token 填入配置文件（见下方示例）',
          '启动 OpenClaw，机器人即可正常响应消息',
        ].map((step, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="w-7 h-7 flex items-center justify-center bg-cyan-500/10 text-cyan-400 rounded-full text-sm font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
            <span>{step}</span>
          </li>
        ))}
      </ol>

      <SectionTitle id="tg-config">配置示例</SectionTitle>
      <CodeBlock language="json5" code={`{
  channels: [
    {
      type: "telegram",
      // 从 BotFather 获取的 Bot Token
      botToken: "123456789:ABCdefGHIjklMNOpqrsTUVwxyz",
      // 私信策略：allow / block
      dmPolicy: "allow",
      // 群组配置（可选）
      groups: {
        // 允许机器人响应的群组 ID 列表（留空表示允许所有）
        allowedIds: [],
        // 是否允许话题（论坛超级群组）
        allowTopics: true,
      },
      // 功能开关
      capabilities: {
        // 是否启用内联按钮
        inlineButtons: true,
        // 是否启用贴纸支持
        stickers: true,
        // 是否启用草稿流式传输
        draftStreaming: false,
      }
    }
  ]
}`} />

      <SectionTitle id="tg-options">配置参考</SectionTitle>
      <ConfigTable rows={[
        { key: 'type', type: 'string', desc: '渠道类型，固定为 "telegram"', default: '"telegram"' },
        { key: 'botToken', type: 'string', desc: '从 BotFather 获取的 Bot Token（必填）' },
        { key: 'dmPolicy', type: 'string', desc: '私信策略：allow（允许）/ block（拒绝）', default: '"allow"' },
        { key: 'groups.allowedIds', type: 'string[]', desc: '允许响应的群组 ID 列表，留空表示允许所有', default: '[]' },
        { key: 'groups.allowTopics', type: 'boolean', desc: '是否允许话题（论坛超级群组）', default: 'true' },
        { key: 'capabilities.inlineButtons', type: 'boolean', desc: '是否启用内联按钮', default: 'true' },
        { key: 'capabilities.stickers', type: 'boolean', desc: '是否启用贴纸支持', default: 'true' },
        { key: 'capabilities.draftStreaming', type: 'boolean', desc: '是否启用草稿流式传输（打字中效果）', default: 'false' },
      ]} />
    </div>
  );
}

function DiscordContent() {
  return (
    <div>
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 flex items-center justify-center bg-indigo-500/10 rounded-xl border border-indigo-500/20">
            <i className="ri-discord-line text-indigo-400 text-2xl"></i>
          </div>
          <div>
            <h1 className="text-4xl font-bold text-white">Discord</h1>
            <div className="flex items-center gap-2 mt-2">
              <Badge color="emerald">稳定版</Badge>
              <Badge color="slate">官方 Bot 网关</Badge>
            </div>
          </div>
        </div>
        <p className="text-slate-400 text-base leading-relaxed">
          通过官方 Discord 机器人网关接入，支持私信和服务器文字频道。
        </p>
      </div>

      <SectionTitle id="dc-intro">简介</SectionTitle>
      <p className="text-slate-300 text-base leading-relaxed mb-4">
        Discord 渠道使用官方 Bot Gateway API，支持私信（DM）和服务器文字频道两种场景。
        需要在 Discord Developer Portal 创建应用并启用 Message Content Intent。
      </p>
      <InfoBox type="warning">
        必须在 Discord Developer Portal 中为机器人启用 <strong>Message Content Intent</strong>，否则机器人无法读取消息内容。
      </InfoBox>

      <SectionTitle id="dc-setup">快速设置</SectionTitle>
      <ol className="space-y-4 text-base text-slate-300 list-none">
        {[
          '访问 Discord Developer Portal，创建新应用',
          '在 Bot 页面创建机器人，获取 Bot Token',
          '在 Bot 页面的 Privileged Gateway Intents 中启用 Message Content Intent',
          '使用 OAuth2 URL 将机器人邀请到您的服务器',
          '将 Bot Token 填入配置文件（见下方示例）',
        ].map((step, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="w-7 h-7 flex items-center justify-center bg-cyan-500/10 text-cyan-400 rounded-full text-sm font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
            <span>{step}</span>
          </li>
        ))}
      </ol>

      <SectionTitle id="dc-config">配置示例</SectionTitle>
      <CodeBlock language="json5" code={`{
  channels: [
    {
      type: "discord",
      // 机器人 Token
      token: "your_bot_token",
      // 私信配置
      dm: {
        // 是否允许私信
        enabled: true,
        // 私信策略：allow / block
        policy: "allow",
      },
      // 服务器配置
      guilds: [
        {
          // 服务器 ID
          id: "123456789012345678",
          // 允许响应的频道 ID 列表（留空表示允许所有）
          channels: [],
        }
      ],
      // 操作审批配置
      actions: {
        // 是否启用执行审批
        requireApproval: false,
      }
    }
  ]
}`} />

      <SectionTitle id="dc-options">配置参考</SectionTitle>
      <ConfigTable rows={[
        { key: 'type', type: 'string', desc: '渠道类型，固定为 "discord"', default: '"discord"' },
        { key: 'token', type: 'string', desc: '机器人 Token（必填）' },
        { key: 'dm.enabled', type: 'boolean', desc: '是否允许私信', default: 'true' },
        { key: 'dm.policy', type: 'string', desc: '私信策略：allow / block', default: '"allow"' },
        { key: 'guilds', type: 'array', desc: '服务器配置列表', default: '[]' },
        { key: 'guilds[].id', type: 'string', desc: '服务器 ID（必填）' },
        { key: 'guilds[].channels', type: 'string[]', desc: '允许响应的频道 ID 列表，留空表示允许所有', default: '[]' },
        { key: 'actions.requireApproval', type: 'boolean', desc: '是否要求执行审批', default: 'false' },
      ]} />
    </div>
  );
}

function SlackContent() {
  return (
    <div>
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 flex items-center justify-center bg-rose-500/10 rounded-xl border border-rose-500/20">
            <i className="ri-slack-line text-rose-400 text-2xl"></i>
          </div>
          <div>
            <h1 className="text-4xl font-bold text-white">Slack</h1>
            <div className="flex items-center gap-2 mt-2">
              <Badge color="emerald">稳定版</Badge>
              <Badge color="slate">Socket 模式 / Events API</Badge>
            </div>
          </div>
        </div>
        <p className="text-slate-400 text-base leading-relaxed">
          支持 Socket 模式（默认）和 HTTP Events API 两种连接方式，需要 App Token 和 Bot Token。
        </p>
      </div>

      <SectionTitle id="sl-intro">简介</SectionTitle>
      <p className="text-slate-300 text-base leading-relaxed mb-4">
        Slack 渠道支持两种连接模式：Socket 模式（默认，无需公网 IP）和 HTTP Events API 模式（需要公网可访问的 HTTPS 地址）。
        需要在 Slack API 控制台创建应用并获取相应的 Token。
      </p>

      <SectionTitle id="sl-setup">快速设置</SectionTitle>
      <ol className="space-y-4 text-base text-slate-300 list-none">
        {[
          '访问 api.slack.com/apps，创建新应用',
          '在 Socket Mode 页面启用 Socket Mode，获取 App-Level Token（以 xapp- 开头）',
          '在 OAuth & Permissions 页面添加所需权限，安装应用到工作区，获取 Bot Token（以 xoxb- 开头）',
          '在 Event Subscriptions 页面订阅所需事件（message.channels、message.im 等）',
          '将 App Token 和 Bot Token 填入配置文件',
        ].map((step, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="w-7 h-7 flex items-center justify-center bg-cyan-500/10 text-cyan-400 rounded-full text-sm font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
            <span>{step}</span>
          </li>
        ))}
      </ol>

      <SectionTitle id="sl-config">配置示例</SectionTitle>
      <CodeBlock language="json5" code={`{
  channels: [
    {
      type: "slack",
      // App-Level Token（以 xapp- 开头，Socket 模式必填）
      appToken: "xapp-1-...",
      // Bot Token（以 xoxb- 开头）
      botToken: "xoxb-...",
      // 群组策略：allow / block
      groupPolicy: "allow",
      // 允许响应的频道 ID 列表（留空表示允许所有）
      channels: [],
      // 是否启用线程回复
      threadReplies: true,
      // 用户令牌（可选，用于某些需要用户权限的操作）
      // userToken: "xoxp-...",
    }
  ]
}`} />

      <SectionTitle id="sl-options">配置参考</SectionTitle>
      <ConfigTable rows={[
        { key: 'type', type: 'string', desc: '渠道类型，固定为 "slack"', default: '"slack"' },
        { key: 'appToken', type: 'string', desc: 'App-Level Token，以 xapp- 开头（Socket 模式必填）' },
        { key: 'botToken', type: 'string', desc: 'Bot Token，以 xoxb- 开头（必填）' },
        { key: 'groupPolicy', type: 'string', desc: '群组策略：allow（允许）/ block（拒绝）', default: '"allow"' },
        { key: 'channels', type: 'string[]', desc: '允许响应的频道 ID 列表，留空表示允许所有', default: '[]' },
        { key: 'threadReplies', type: 'boolean', desc: '是否在线程中回复消息', default: 'true' },
        { key: 'userToken', type: 'string', desc: '用户令牌，以 xoxp- 开头（可选）' },
      ]} />

      <SectionTitle id="sl-modes">连接模式</SectionTitle>
      <SubTitle>Socket 模式（推荐）</SubTitle>
      <p className="text-slate-300 text-base leading-relaxed mb-4">
        Socket 模式通过 WebSocket 连接接收事件，无需公网 IP 或 HTTPS 证书，适合本地部署。
        需要在 Slack 应用设置中启用 Socket Mode 并生成 App-Level Token。
      </p>
      <SubTitle>HTTP Events API 模式</SubTitle>
      <p className="text-slate-300 text-base leading-relaxed mb-4">
        HTTP 模式通过 Webhook 接收事件，需要公网可访问的 HTTPS 地址。适合云端部署场景。
      </p>
      <CodeBlock language="json5" code={`{
  type: "slack",
  botToken: "xoxb-...",
  // 使用 HTTP 模式时，不需要 appToken
  // 需要在 Slack 应用设置中配置 Request URL
  httpMode: {
    enabled: true,
    port: 3000,
    path: "/slack/events",
    signingSecret: "your_signing_secret",
  }
}`} />
    </div>
  );
}

function MattermostContent() {
  return (
    <div>
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 flex items-center justify-center bg-teal-500/10 rounded-xl border border-teal-500/20">
            <i className="ri-chat-3-line text-teal-400 text-2xl"></i>
          </div>
          <div>
            <h1 className="text-4xl font-bold text-white">Mattermost</h1>
            <div className="flex items-center gap-2 mt-2">
              <Badge color="emerald">稳定版</Badge>
              <Badge color="amber">需要安装插件</Badge>
            </div>
          </div>
        </div>
        <p className="text-slate-400 text-base leading-relaxed">
          以插件形式提供，需要单独安装 @openclaw/mattermost 插件。
        </p>
      </div>

      <SectionTitle id="mm-intro">简介</SectionTitle>
      <p className="text-slate-300 text-base leading-relaxed mb-4">
        Mattermost 渠道以 OpenClaw 插件形式提供，需要先安装插件才能使用。
        支持 oncall、onmessage、onchar 三种聊天模式，适合企业内部部署场景。
      </p>

      <SectionTitle id="mm-install">安装插件</SectionTitle>
      <p className="text-slate-300 text-base leading-relaxed mb-4">
        使用以下命令安装 Mattermost 插件：
      </p>
      <CodeBlock language="bash" code={`openclaw plugins install @openclaw/mattermost`} />
      <InfoBox type="info">
        安装插件后需要重启 OpenClaw 才能生效。
      </InfoBox>

      <SectionTitle id="mm-setup">快速设置</SectionTitle>
      <ol className="space-y-4 text-base text-slate-300 list-none">
        {[
          '安装 @openclaw/mattermost 插件',
          '在 Mattermost 中创建机器人账户，获取 Bot Token',
          '记录 Mattermost 服务器地址（baseUrl）',
          '将配置信息填入配置文件（见下方示例）',
          '重启 OpenClaw',
        ].map((step, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="w-7 h-7 flex items-center justify-center bg-cyan-500/10 text-cyan-400 rounded-full text-sm font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
            <span>{step}</span>
          </li>
        ))}
      </ol>

      <SectionTitle id="mm-config">配置示例</SectionTitle>
      <CodeBlock language="json5" code={`{
  channels: [
    {
      type: "mattermost",
      // 机器人 Token
      botToken: "your_bot_token",
      // Mattermost 服务器地址
      baseUrl: "https://your-mattermost.example.com",
      // 私信策略：allow / block
      dmPolicy: "allow",
      // 聊天模式：oncall / onmessage / onchar
      chatmode: "onmessage",
      // 允许响应的频道 ID 列表（留空表示允许所有）
      channels: [],
    }
  ]
}`} />

      <SectionTitle id="mm-options">配置参考</SectionTitle>
      <ConfigTable rows={[
        { key: 'type', type: 'string', desc: '渠道类型，固定为 "mattermost"', default: '"mattermost"' },
        { key: 'botToken', type: 'string', desc: '机器人 Token（必填）' },
        { key: 'baseUrl', type: 'string', desc: 'Mattermost 服务器地址（必填）' },
        { key: 'dmPolicy', type: 'string', desc: '私信策略：allow（允许）/ block（拒绝）', default: '"allow"' },
        { key: 'chatmode', type: 'string', desc: '聊天模式：oncall（呼叫触发）/ onmessage（消息触发）/ onchar（字符触发）', default: '"onmessage"' },
        { key: 'channels', type: 'string[]', desc: '允许响应的频道 ID 列表，留空表示允许所有', default: '[]' },
      ]} />
    </div>
  );
}

function SignalContent() {
  return (
    <div>
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 flex items-center justify-center bg-cyan-500/10 rounded-xl border border-cyan-500/20">
            <i className="ri-shield-keyhole-line text-cyan-400 text-2xl"></i>
          </div>
          <div>
            <h1 className="text-4xl font-bold text-white">Signal</h1>
            <div className="flex items-center gap-2 mt-2">
              <Badge color="emerald">稳定版</Badge>
              <Badge color="amber">需要 signal-cli</Badge>
            </div>
          </div>
        </div>
        <p className="text-slate-400 text-base leading-relaxed">
          通过 signal-cli 外部 CLI 工具集成，需要 Java 运行环境。
        </p>
      </div>

      <SectionTitle id="sg-intro">简介</SectionTitle>
      <p className="text-slate-300 text-base leading-relaxed mb-4">
        Signal 渠道通过 signal-cli 命令行工具与 Signal 服务器通信。
        signal-cli 是一个开源的 Signal 客户端，支持以守护进程模式运行，
        OpenClaw 通过 HTTP API 与其通信。
      </p>

      <SectionTitle id="sg-prereq">前置要求</SectionTitle>
      <ul className="space-y-3 text-base text-slate-300">
        <li className="flex items-start gap-3">
          <i className="ri-checkbox-circle-line text-cyan-400 mt-1 flex-shrink-0 text-lg"></i>
          <span><strong className="text-white">Java 运行环境：</strong>signal-cli 需要 Java 17 或更高版本。</span>
        </li>
        <li className="flex items-start gap-3">
          <i className="ri-checkbox-circle-line text-cyan-400 mt-1 flex-shrink-0 text-lg"></i>
          <span><strong className="text-white">signal-cli：</strong>从 <a href="https://github.com/AsamK/signal-cli" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">GitHub</a> 下载并安装 signal-cli。</span>
        </li>
        <li className="flex items-start gap-3">
          <i className="ri-checkbox-circle-line text-cyan-400 mt-1 flex-shrink-0 text-lg"></i>
          <span><strong className="text-white">Signal 账号：</strong>需要一个已注册的 Signal 账号（手机号）。</span>
        </li>
      </ul>

      <SectionTitle id="sg-setup">快速设置</SectionTitle>
      <ol className="space-y-4 text-base text-slate-300 list-none">
        {[
          '安装 Java 17+ 和 signal-cli',
          '使用 signal-cli 注册或链接 Signal 账号',
          '以守护进程模式启动 signal-cli（监听 HTTP 端口）',
          '将账号和 signal-cli 路径填入配置文件',
        ].map((step, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="w-7 h-7 flex items-center justify-center bg-cyan-500/10 text-cyan-400 rounded-full text-sm font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
            <span>{step}</span>
          </li>
        ))}
      </ol>
      <CodeBlock language="bash" code={`# 以守护进程模式启动 signal-cli
signal-cli -a +1234567890 daemon --http 127.0.0.1:8080`} />

      <SectionTitle id="sg-config">配置示例</SectionTitle>
      <CodeBlock language="json5" code={`{
  channels: [
    {
      type: "signal",
      // Signal 账号（手机号，含国际区号）
      account: "+1234567890",
      // signal-cli 可执行文件路径
      cliPath: "/usr/local/bin/signal-cli",
      // signal-cli HTTP 守护进程地址
      httpUrl: "http://127.0.0.1:8080",
      // 私信策略：allow / block
      dmPolicy: "allow",
    }
  ]
}`} />

      <SectionTitle id="sg-options">配置参考</SectionTitle>
      <ConfigTable rows={[
        { key: 'type', type: 'string', desc: '渠道类型，固定为 "signal"', default: '"signal"' },
        { key: 'account', type: 'string', desc: 'Signal 账号（手机号，含国际区号，必填）' },
        { key: 'cliPath', type: 'string', desc: 'signal-cli 可执行文件路径（必填）' },
        { key: 'httpUrl', type: 'string', desc: 'signal-cli HTTP 守护进程地址', default: '"http://127.0.0.1:8080"' },
        { key: 'dmPolicy', type: 'string', desc: '私信策略：allow（允许）/ block（拒绝）', default: '"allow"' },
      ]} />
    </div>
  );
}

function IMessageContent() {
  return (
    <div>
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 flex items-center justify-center bg-green-500/10 rounded-xl border border-green-500/20">
            <i className="ri-message-2-line text-green-400 text-2xl"></i>
          </div>
          <div>
            <h1 className="text-4xl font-bold text-white">iMessage</h1>
            <div className="flex items-center gap-2 mt-2">
              <Badge color="emerald">稳定版</Badge>
              <Badge color="amber">仅 macOS</Badge>
              <Badge color="slate">通过 imsg</Badge>
            </div>
          </div>
        </div>
        <p className="text-slate-400 text-base leading-relaxed">
          通过 imsg 外部 CLI 工具集成，仅支持 macOS，需要完全磁盘访问权限。
        </p>
      </div>

      <SectionTitle id="im-intro">简介</SectionTitle>
      <p className="text-slate-300 text-base leading-relaxed mb-4">
        iMessage 渠道通过 imsg 命令行工具读取 macOS 上的 iMessage 数据库，
        实现消息的接收和发送。由于 iMessage 的限制，此渠道仅支持 macOS 系统。
      </p>
      <InfoBox type="warning">
        iMessage 渠道仅支持 macOS，且需要授予完全磁盘访问权限（Full Disk Access）才能读取消息数据库。
      </InfoBox>

      <SectionTitle id="im-prereq">前置要求</SectionTitle>
      <ul className="space-y-3 text-base text-slate-300">
        <li className="flex items-start gap-3">
          <i className="ri-checkbox-circle-line text-cyan-400 mt-1 flex-shrink-0 text-lg"></i>
          <span><strong className="text-white">macOS 系统：</strong>仅支持 macOS，不支持 iOS 或其他系统。</span>
        </li>
        <li className="flex items-start gap-3">
          <i className="ri-checkbox-circle-line text-cyan-400 mt-1 flex-shrink-0 text-lg"></i>
          <span><strong className="text-white">完全磁盘访问权限：</strong>在系统偏好设置 &gt; 安全性与隐私 &gt; 完全磁盘访问中授权。</span>
        </li>
        <li className="flex items-start gap-3">
          <i className="ri-checkbox-circle-line text-cyan-400 mt-1 flex-shrink-0 text-lg"></i>
          <span><strong className="text-white">imsg 工具：</strong>需要安装 imsg CLI 工具。</span>
        </li>
        <li className="flex items-start gap-3">
          <i className="ri-checkbox-circle-line text-cyan-400 mt-1 flex-shrink-0 text-lg"></i>
          <span><strong className="text-white">已登录 iMessage：</strong>Mac 上需要已登录 Apple ID 并启用 iMessage。</span>
        </li>
      </ul>

      <SectionTitle id="im-setup">快速设置</SectionTitle>
      <ol className="space-y-4 text-base text-slate-300 list-none">
        {[
          '安装 imsg CLI 工具',
          '在系统偏好设置中授予完全磁盘访问权限',
          '确认 iMessage 数据库路径（通常为 ~/Library/Messages/chat.db）',
          '将配置信息填入配置文件（见下方示例）',
        ].map((step, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="w-7 h-7 flex items-center justify-center bg-cyan-500/10 text-cyan-400 rounded-full text-sm font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
            <span>{step}</span>
          </li>
        ))}
      </ol>

      <SectionTitle id="im-config">配置示例</SectionTitle>
      <CodeBlock language="json5" code={`{
  channels: [
    {
      type: "imessage",
      // imsg CLI 工具路径
      cliPath: "/usr/local/bin/imsg",
      // iMessage 数据库路径
      dbPath: "~/Library/Messages/chat.db",
      // 私信策略：allow / block
      dmPolicy: "allow",
      // 远程/SSH 变体配置（可选，用于远程 Mac）
      // remoteHost: "user@remote-mac.local",
      // Tailscale 连接（可选）
      // tailscale: {
      //   enabled: true,
      //   hostname: "my-mac",
      // }
    }
  ]
}`} />

      <SectionTitle id="im-options">配置参考</SectionTitle>
      <ConfigTable rows={[
        { key: 'type', type: 'string', desc: '渠道类型，固定为 "imessage"', default: '"imessage"' },
        { key: 'cliPath', type: 'string', desc: 'imsg CLI 工具路径（必填）' },
        { key: 'dbPath', type: 'string', desc: 'iMessage 数据库路径', default: '"~/Library/Messages/chat.db"' },
        { key: 'dmPolicy', type: 'string', desc: '私信策略：allow（允许）/ block（拒绝）', default: '"allow"' },
        { key: 'remoteHost', type: 'string', desc: '远程 Mac 的 SSH 地址（可选，用于远程部署）' },
        { key: 'tailscale.enabled', type: 'boolean', desc: '是否通过 Tailscale 连接远程 Mac', default: 'false' },
        { key: 'tailscale.hostname', type: 'string', desc: 'Tailscale 主机名（启用 Tailscale 时必填）' },
      ]} />
    </div>
  );
}

const contentMap: Record<SectionId, React.ReactNode> = {
  overview: <OverviewContent />,
  whatsapp: <WhatsAppContent />,
  telegram: <TelegramContent />,
  discord: <DiscordContent />,
  slack: <SlackContent />,
  mattermost: <MattermostContent />,
  signal: <SignalContent />,
  imessage: <IMessageContent />,
};

export default function ChannelsPage() {
  const [activeSection, setActiveSection] = useState<SectionId>(() => {
    const saved = localStorage.getItem('channels_active_section');
    return (saved as SectionId) || 'overview';
  });
  const [activeToc, setActiveToc] = useState<string>('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const currentToc = tocMap[activeSection];
  const currentNavItem = navItems.find(item => item.id === activeSection);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://openclaw.cc';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: '消息渠道配置',
    description: 'WhatsApp、Telegram、Discord、iMessage 等渠道接入指南 - OpenClaw 支持 20+ 消息平台',
    url: `${siteUrl}/channels`,
    datePublished: '2024-01-01',
    dateModified: new Date().toISOString().split('T')[0],
    author: {
      '@type': 'Organization',
      name: 'OpenClaw',
    },
    publisher: {
      '@type': 'Organization',
      name: 'OpenClaw',
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/lobster.svg`,
      },
    },
    inLanguage: 'zh-CN',
  };

  // 监听导航栏子菜单点击
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'channels_active_section' && e.newValue) {
        setActiveSection(e.newValue as SectionId);
      }
    };

    const handleNavChange = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.storageKey === 'channels_active_section') {
        setActiveSection(detail.key as SectionId);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('nav-section-change', handleNavChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('nav-section-change', handleNavChange);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('channels_active_section', activeSection);
    setActiveToc(currentToc[0]?.id || '');
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [activeSection]);

  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;
    const handleScroll = () => {
      const headings = currentToc.map((t) => document.getElementById(t.id)).filter(Boolean) as HTMLElement[];
      let current = headings[0]?.id || '';
      for (const el of headings) {
        if (el.getBoundingClientRect().top < 120) {
          current = el.id;
        }
      }
      setActiveToc(current);
    };
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [activeSection, currentToc]);

  const handleSectionChange = (e: React.MouseEvent, id: SectionId) => {
    e.preventDefault();
    setActiveSection(id);
    setMobileMenuOpen(false);
  };

  return (
    <PageTransition>
      <SEOHead
        title="消息渠道配置 | WhatsApp、Telegram、Discord 接入指南"
        description="WhatsApp、Telegram、Discord、iMessage 等渠道接入指南 - OpenClaw 支持 20+ 消息平台，轻松接入各类即时通讯工具"
        keywords="消息渠道配置, WhatsApp接入, Telegram Bot, Discord机器人, iMessage集成"
        canonical={`${siteUrl}/channels`}
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-slate-950 flex flex-col">
        <Navbar />

        {/* Mobile Section Selector */}
        <div className="md:hidden sticky top-16 z-40 bg-slate-900 border-b border-slate-800">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="w-full flex items-center justify-between px-4 py-3 text-left cursor-pointer"
          >
            <div className="flex items-center gap-2.5">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${currentNavItem?.icon} text-cyan-400`}></i>
              </div>
              <span className="text-white font-medium text-sm">{currentNavItem?.label}</span>
            </div>
            <i className={`ri-arrow-down-s-line text-slate-400 transition-transform duration-200 ${mobileMenuOpen ? 'rotate-180' : ''}`}></i>
          </button>

          {/* Mobile Dropdown Menu */}
          {mobileMenuOpen && (
            <div className="absolute top-full left-0 right-0 bg-slate-900 border-b border-slate-800 max-h-[70vh] overflow-y-auto">
              <nav className="p-3">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={(e) => handleSectionChange(e, item.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap mb-0.5 text-left ${
                      activeSection === item.id
                        ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
                    }`}
                  >
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-base`}></i>
                    </div>
                    {item.label}
                  </button>
                ))}
              </nav>
            </div>
          )}
        </div>

        <div className="flex flex-1 pt-16" style={{ height: 'calc(100vh - 0px)' }}>
          {/* Main Content */}
          <main
            ref={contentRef}
            className="flex-1 overflow-y-auto"
            style={{ height: 'calc(100vh - 64px)' }}
          >
            <div className="max-w-6xl mx-auto px-10 md:px-20 py-14 min-h-full">
              {contentMap[activeSection]}
            </div>
            <Footer />
          </main>
        </div>
      </div>
    </PageTransition>
  );
}
